import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export const DL_MINUTES_PER_DAY = 1440;
export const DL_WEEK_MINUTES = DL_MINUTES_PER_DAY * 7;

export const DL_DAYS_OF_WEEK = [
  { index: 0, label: 'Lunes', value: 64, short: 'L' },
  { index: 1, label: 'Martes', value: 32, short: 'M' },
  { index: 2, label: 'Miércoles', value: 16, short: 'X' },
  { index: 3, label: 'Jueves', value: 8, short: 'J' },
  { index: 4, label: 'Viernes', value: 4, short: 'V' },
  { index: 5, label: 'Sábado', value: 2, short: 'S' },
  { index: 6, label: 'Domingo', value: 1, short: 'D' },
] as const;

export type DlProgramKind = 'off' | 'on' | 'pulse' | 'cycle';

export interface DlProgram {
  id: number;
  channel: number;
  type: number;
  mode: number;
  daymask: number;
  festive: number;
  time: number;
  auxdata: number[];
  kind: DlProgramKind;
}

export interface DlPulseRegion {
  programId: number;
  weekMinuteStart: number;
  weekMinuteEnd: number;
  centerWeekMinute: number;
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  startSecondOfDay: number;
  durationSeconds: number;
  timeLabel: string;
  timeSecondLabel: string;
  dayLabel: string;
}

type CycleDaySegment = 'start' | 'end' | 'both' | 'middle';

interface CycleDayRange {
  start: number;
  end: number;
  segment: CycleDaySegment;
}

export interface DlCycleRegion {
  programId: number;
  weekMinuteStart: number;
  weekMinuteEnd: number;
  centerWeekMinute: number;
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  startSecondOfDay: number;
  endSecondOfDay: number;
  onSeconds: number;
  offSeconds: number;
  overnight: boolean;
  cyclePhaseOffsetSeconds: number;
  timeLabel: string;
  endTimeLabel: string;
  dayLabel: string;
}

interface TimelineEvent {
  minute: number;
  value: 0 | 1;
  priority: number;
}

@Injectable({ providedIn: 'root' })
export class DlScheduleService {

  constructor(private translate: TranslateService) {}

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  parsePrograms(programData: any): DlProgram[] {
    const programs: DlProgram[] = [];
    if (!programData || typeof programData !== 'object') return programs;

    const numericKeys = Object.keys(programData).filter((key) => /^\d+$/.test(key));
    numericKeys.sort((a, b) => Number(a) - Number(b));

    numericKeys.forEach((key) => {
      const parsed = this.parseRawProgramArray(programData[key]);
      if (!parsed || parsed.length < 10) return;

      const id = Number(parsed[4]);
      if (!Number.isFinite(id) || id > 39) return;

      const type = Number(parsed[6]) || 0;
      const mode = Number(parsed[7]) || 0;
      const time = this.decodeTimeBytes(parsed);

      programs.push({
        id,
        channel: parsed[5],
        type,
        mode,
        daymask: (Number(parsed[8]) || 0) & 0x7f,
        festive: parsed[9],
        time,
        auxdata: parsed.slice(10, 19).map((value) => Number(value) || 0),
        kind: this.resolveProgramKind(type, mode),
      });
    });

    return programs;
  }

  buildWeekChartData(
    programs: DlProgram[],
    activeFestive = 0,
    channelFilter: 1 | 2
  ): number[] {
    return this.buildWeekChartDataForDays(
      programs,
      Array(7).fill(Number(activeFestive) || 0),
      channelFilter
    );
  }

  buildWeekChartDataForDays(
    programs: DlProgram[],
    festiveByDayIndex: number[] = [0, 0, 0, 0, 0, 0, 0],
    channelFilter: 1 | 2
  ): number[] {
    const computeWeek = (initialMonday: 0 | 1): { data: number[]; endValue: 0 | 1 } => {
      const data = new Array(DL_WEEK_MINUTES).fill(0);
      let prevValue: 0 | 1 = initialMonday;

      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const dayBit = DL_DAYS_OF_WEEK[dayIndex].value;
        const activeFestive = Number(festiveByDayIndex[dayIndex]) || 0;
        const events = this.buildEventsForDay(programs, dayBit, activeFestive, channelFilter);
        let current: 0 | 1 = prevValue;
        let eventIndex = 0;
        const offset = dayIndex * DL_MINUTES_PER_DAY;

        for (let minute = 0; minute < DL_MINUTES_PER_DAY; minute++) {
          while (eventIndex < events.length && events[eventIndex].minute <= minute) {
            current = events[eventIndex].value;
            eventIndex++;
          }
          data[offset + minute] = current;
        }

        this.applyPulseOverlaysForDay(
          data,
          offset,
          programs,
          dayBit,
          activeFestive,
          channelFilter
        );

        this.applyCycleOverlaysForDay(
          data,
          offset,
          programs,
          dayIndex,
          activeFestive,
          channelFilter
        );

        prevValue = data[offset + DL_MINUTES_PER_DAY - 1] as 0 | 1;
      }

      return { data, endValue: prevValue };
    };

    const pass1 = computeWeek(0);
    const pass2 = computeWeek(pass1.endValue);
    return pass2.data;
  }

  buildDayChartData(
    programs: DlProgram[],
    dayIndex: number,
    activeFestive = 0,
    channelFilter: 1 | 2,
    weekData?: number[]
  ): number[] {
    const source = weekData ?? this.buildWeekChartDataForDays(
      programs,
      Array(7).fill(Number(activeFestive) || 0),
      channelFilter
    );
    const offset = dayIndex * DL_MINUTES_PER_DAY;
    return source.slice(offset, offset + DL_MINUTES_PER_DAY);
  }

  getPulseRegions(
    programs: DlProgram[],
    activeFestive = 0,
    channelFilter: 1 | 2
  ): DlPulseRegion[] {
    const regions: DlPulseRegion[] = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayBit = DL_DAYS_OF_WEEK[dayIndex].value;
      const dayOffset = dayIndex * DL_MINUTES_PER_DAY;

      (programs || []).forEach((program) => {
        if (!this.isPulseProgram(program)) return;
        if (!this.matchesProgramFilters(program, dayBit, activeFestive, channelFilter)) return;

        const range = this.getPulseMinuteRange(program);
        if (!range) return;

        const weekMinuteStart = dayOffset + range.start;
        const weekMinuteEnd = dayOffset + range.end;
        const startSecondOfDay = range.start * 60;
        const durationSeconds = this.resolvePulseDurationSeconds(program);

        regions.push({
          programId: program.id,
          weekMinuteStart,
          weekMinuteEnd,
          centerWeekMinute: weekMinuteStart + ((range.end - range.start) / 2),
          startMinuteOfDay: range.start,
          endMinuteOfDay: range.end,
          startSecondOfDay,
          durationSeconds,
          timeLabel: this.formatMinuteLabel(range.start),
          timeSecondLabel: this.formatSecondLabel(startSecondOfDay),
          dayLabel: DL_DAYS_OF_WEEK[dayIndex].short,
        });
      });
    }

    return regions;
  }

  getCycleRegions(
    programs: DlProgram[],
    activeFestive = 0,
    channelFilter: 1 | 2
  ): DlCycleRegion[] {
    const regions: DlCycleRegion[] = [];

    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayBit = DL_DAYS_OF_WEEK[dayIndex].value;
      const dayOffset = dayIndex * DL_MINUTES_PER_DAY;

      (programs || []).forEach((program) => {
        if (!this.isCycleProgram(program)) return;
        if (!this.matchesChannelAndFestive(program, activeFestive, channelFilter)) return;

        const timing = this.resolveCycleTiming(program);
        if (!timing) return;

        const { startDayIndex, endDayIndex } = this.resolveCycleDayIndices(program);
        const dayRanges = this.getCycleMinuteRangesForDay(program, dayIndex, timing);
        if (!dayRanges.length) return;

        dayRanges.forEach((range) => {
          const weekMinuteStart = dayOffset + range.start;
          const weekMinuteEnd = dayOffset + range.end;

          regions.push({
            programId: program.id,
            weekMinuteStart,
            weekMinuteEnd,
            centerWeekMinute: weekMinuteStart + ((range.end - range.start) / 2),
            startMinuteOfDay: range.start,
            endMinuteOfDay: range.end,
            startSecondOfDay: range.start * 60,
            endSecondOfDay: range.end * 60,
            onSeconds: timing.onSeconds,
            offSeconds: timing.offSeconds,
            overnight: timing.overnight,
            cyclePhaseOffsetSeconds: this.getCyclePhaseOffsetSeconds(
              program,
              dayIndex,
              range,
              startDayIndex,
              endDayIndex,
              timing.startMinute
            ),
            timeLabel: this.formatMinuteLabel(range.start),
            endTimeLabel: this.formatMinuteLabel(range.end),
            dayLabel: DL_DAYS_OF_WEEK[dayIndex].short,
          });
        });
      });
    }

    return regions;
  }

  getCycleValueAtSecond(secondOfDay: number, region: DlCycleRegion): 0 | 1 {
    if (secondOfDay < region.startSecondOfDay || secondOfDay >= region.endSecondOfDay) {
      return 0;
    }

    const onSeconds = Math.max(1, region.onSeconds || 1);
    const offSeconds = Math.max(1, region.offSeconds || 1);
    const elapsed = (secondOfDay - region.startSecondOfDay) + (region.cyclePhaseOffsetSeconds || 0);
    const period = onSeconds + offSeconds;
    const phase = ((elapsed % period) + period) % period;
    return phase < onSeconds ? 1 : 0;
  }

  formatMinuteLabel(minuteOfDay: number): string {
    const safeMinute = this.clampMinute(minuteOfDay);
    const hh = String(Math.floor(safeMinute / 60)).padStart(2, '0');
    const mm = String(safeMinute % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  formatSecondLabel(secondOfDay: number, includeSeconds = true): string {
    const safeSecond = Math.max(0, Math.floor(Number(secondOfDay) || 0));
    const hh = String(Math.floor(safeSecond / 3600)).padStart(2, '0');
    const mm = String(Math.floor((safeSecond % 3600) / 60)).padStart(2, '0');
    const ss = String(safeSecond % 60).padStart(2, '0');
    return includeSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
  }

  formatDurationLabel(seconds: number): string {
    const safeSeconds = Math.max(0, Number(seconds) || 0);

    if (safeSeconds < 60) {
      return this.t('durationSeconds', { value: Math.round(safeSeconds) });
    }

    const minutes = safeSeconds / 60;
    if (Math.abs(minutes - Math.round(minutes)) < 0.05) {
      return this.t('durationMinutes', { value: Math.round(minutes) });
    }

    const decimalSep = this.translate.currentLang === 'es' ? ',' : '.';
    return this.t('durationMinutes', { value: minutes.toFixed(1).replace('.', decimalSep) });
  }

  formatPulseDurationLabel(seconds: number): string {
    const safeSeconds = Math.max(0, Math.trunc(Number(seconds) || 0));
    return this.t('durationSeconds', { value: safeSeconds });
  }

  private parseRawProgramArray(raw: any): number[] | null {
    if (raw == null) return null;

    if (Array.isArray(raw)) {
      return raw.map((value) => Number(value));
    }

    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed.map((value) => Number(value));
      } catch {
        return null;
      }
    }

    return null;
  }

  private decodeTimeBytes(parsed: number[]): number {
    const unsigned =
      ((Number(parsed[0]) & 0xff) << 24) |
      ((Number(parsed[1]) & 0xff) << 16) |
      ((Number(parsed[2]) & 0xff) << 8) |
      (Number(parsed[3]) & 0xff);

    return unsigned | 0;
  }

  private resolveProgramKind(type: number, mode: number): DlProgramKind {
    if (this.isCycleType(type, mode)) return 'cycle';
    if (this.isPulseType(type, mode)) return 'pulse';
    if (type === 1) return 'on';
    return 'off';
  }

  /** Detecta programas pulso aunque falte kind (p. ej. tras cambiar mode a 64). */
  isPulseProgram(program: Pick<DlProgram, 'kind' | 'type' | 'mode'>): boolean {
    if (this.isCycleProgram(program)) return false;
    return this.isPulseType(Number(program.type) || 0, Number(program.mode) || 0)
      || program.kind === 'pulse';
  }

  /** Detecta programas ciclo aunque falte kind. */
  isCycleProgram(program: Pick<DlProgram, 'kind' | 'type' | 'mode'>): boolean {
    return this.isCycleType(Number(program.type) || 0, Number(program.mode) || 0)
      || program.kind === 'cycle';
  }

  private isPulseType(type: number, mode: number): boolean {
    return type === 2 || (mode & 64) !== 0;
  }

  private isCycleType(type: number, mode: number): boolean {
    return type === 3 || (mode & 128) !== 0;
  }

  private clampMinute(minute: number): number {
    return Math.max(0, Math.min(DL_MINUTES_PER_DAY - 1, Math.trunc(minute)));
  }

  private matchesChannel(channel: number, channelFilter: 1 | 2): boolean {
    if (channelFilter === 1) return channel === 1 || channel === 3;
    return channel === 2 || channel === 3;
  }

  private matchesChannelAndFestive(
    program: DlProgram,
    activeFestive: number,
    channelFilter: 1 | 2
  ): boolean {
    const festive = Number(program.festive) || 0;
    if (festive !== Number(activeFestive || 0)) return false;

    const channel = Number(program.channel) || 0;
    return this.matchesChannel(channel, channelFilter);
  }

  private matchesProgramFilters(
    program: DlProgram,
    targetDayBit: number,
    activeFestive: number,
    channelFilter: 1 | 2
  ): boolean {
    const daymask = Number(program.daymask) || 0;
    if ((daymask & targetDayBit) === 0) return false;

    return this.matchesChannelAndFestive(program, activeFestive, channelFilter);
  }

  /** daymask fin de ciclo (auxdata[0]); si no está definido, usa el daymask de inicio. */
  getCycleEndDaymask(program: DlProgram): number {
    const endDaymask = Number(program.auxdata?.[0]) || 0;
    if (endDaymask > 0) return endDaymask & 0x7f;
    return (Number(program.daymask) || 0) & 0x7f;
  }

  /** Ciclo que cruza el domingo (p. ej. V → L). Distinto de hora fin < hora inicio en reloj. */
  isCycleWeekWrap(program: DlProgram): boolean {
    const { startDayIndex, endDayIndex } = this.resolveCycleDayIndices(program);
    return startDayIndex >= 0 && endDayIndex >= 0 && startDayIndex > endDayIndex;
  }

  private resolveFixedMinute(time: number, mode: number, type = 0): number | null {
    const safeTime = Math.trunc(Number(time) || 0);
    const isPulseOrCycle =
      type === 2 ||
      type === 3 ||
      (mode & 64) !== 0 ||
      (mode & 128) !== 0;

    // En pulsos/ciclos DL, valores 0-23 suelen representar la hora en punto.
    if (isPulseOrCycle) {
      if (safeTime >= 0 && safeTime <= 23) {
        return this.clampMinute(safeTime * 60);
      }
    }

    // Valores canónicos del firmware: 0..1439 minutos.
    if (safeTime >= 0 && safeTime < DL_MINUTES_PER_DAY) {
      return this.clampMinute(safeTime);
    }

    // HHMM compacto solo fuera del rango de minutos, ej. 2000 = 20:00.
    if (safeTime > DL_MINUTES_PER_DAY && safeTime <= 2359) {
      const hh = Math.floor(safeTime / 100);
      const mm = safeTime % 100;
      if (hh >= 0 && hh < 24 && mm >= 0 && mm < 60) {
        return this.clampMinute(hh * 60 + mm);
      }
    }

    return null;
  }

  private resolveStartMinute(program: DlProgram): number | null {
    const mode = Number(program.mode) || 0;
    const type = Number(program.type) || 0;
    const time = Number(program.time) || 0;

    if (
      mode === 16 ||
      mode === 64 ||
      mode === 128 ||
      (mode & 64) !== 0 ||
      (mode & 128) !== 0 ||
      program.kind === 'pulse' ||
      program.kind === 'cycle'
    ) {
      return this.resolveFixedMinute(time, mode, type);
    }

    if (time >= 0 && time < DL_MINUTES_PER_DAY) {
      return this.clampMinute(time);
    }

    return null;
  }

  private pack24(byte0: number, byte1: number, byte2: number): number {
    return (
      ((Number(byte0) & 0xff) << 16) |
      ((Number(byte1) & 0xff) << 8) |
      (Number(byte2) & 0xff)
    );
  }

  /** Convierte HH:mm o HH:mm:ss (p. ej. input type="time") a minutos del día. */
  parseTimeInputToMinutes(value: unknown): number | null {
    if (typeof value !== 'string') return null;

    const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

    return hours * 60 + minutes;
  }

  /** Decodifica segundos ON/OFF desde 3 bytes (uint24 BE del firmware). */
  private unpackSeconds24(byte0: number, byte1: number, byte2: number): number {
    const b0 = Number(byte0) & 0xff;
    const b1 = Number(byte1) & 0xff;
    const b2 = Number(byte2) & 0xff;
    const packed = this.pack24(b0, b1, b2);

    if (packed > 0) return packed;

    // Compatibilidad: algunos firmwares guardan el valor en el primer byte.
    if (b0 > 0) return b0;
    if (b1 > 0) return b1;
    if (b2 > 0) return b2;

    return 0;
  }

  private resolvePulseDurationSeconds(program: DlProgram): number {
    const auxdata = program.auxdata || [];

    const direct = Number(auxdata[0]) || 0;
    if (direct > 0) return direct;

    const packedShort = this.pack24(0, 0, auxdata[0]);
    if (packedShort > 0) return packedShort;

    return this.pack24(auxdata[0], auxdata[1], auxdata[2]);
  }

  private resolveEndMinute(auxdata: number[]): number | null {
    if (!auxdata || auxdata.length < 3) return null;

    const packed = ((Number(auxdata[1]) & 0xff) << 8) | (Number(auxdata[2]) & 0xff);
    if (packed >= 0 && packed < DL_MINUTES_PER_DAY) {
      return packed;
    }

    // Compatibilidad con datos antiguos guardados como [hora, minuto].
    const hour = Number(auxdata[1]) || 0;
    const minute = Number(auxdata[2]) || 0;
    if (hour >= 0 && hour < 24 && minute >= 0 && minute < 60) {
      const fromHourMinute = (hour * 60) + minute;
      if (fromHourMinute < DL_MINUTES_PER_DAY) {
        return fromHourMinute;
      }
    }

    return null;
  }

  private secondsToChartMinutes(seconds: number): number {
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    if (safeSeconds === 0) return 0;
    return Math.max(1, Math.ceil(safeSeconds / 60));
  }

  private getPulseMinuteRange(program: DlProgram): { start: number; end: number } | null {
    const startMinute = this.resolveStartMinute(program);
    if (startMinute === null) return null;

    const durationMinutes = this.secondsToChartMinutes(this.resolvePulseDurationSeconds(program));
    if (durationMinutes === 0) return null;

    return {
      start: startMinute,
      end: Math.min(DL_MINUTES_PER_DAY, startMinute + durationMinutes),
    };
  }

  private resolveCycleTiming(program: DlProgram): {
    startMinute: number;
    endMinute: number;
    onSeconds: number;
    offSeconds: number;
    overnight: boolean;
  } | null {
    const startMinute = this.resolveStartMinute(program);
    const endMinute = this.resolveEndMinute(program.auxdata);
    if (startMinute === null || endMinute === null) return null;

    const onSeconds = this.unpackSeconds24(program.auxdata[3], program.auxdata[4], program.auxdata[5]);
    const offSeconds = this.unpackSeconds24(program.auxdata[6], program.auxdata[7], program.auxdata[8]);
    if (onSeconds <= 0 && offSeconds <= 0) return null;

    return {
      startMinute,
      endMinute,
      onSeconds: Math.max(1, onSeconds),
      offSeconds: Math.max(1, offSeconds),
      overnight: endMinute <= startMinute,
    };
  }

  private resolveCycleDayIndices(program: DlProgram): { startDayIndex: number; endDayIndex: number } {
    const daymask = Number(program.daymask) || 0;
    const endDaymask = this.getCycleEndDaymask(program);
    return {
      startDayIndex: this.getFirstDayIndexInMask(daymask),
      endDayIndex: this.getLastDayIndexInMask(endDaymask),
    };
  }

  private getFirstDayIndexInMask(mask: number): number {
    for (const day of DL_DAYS_OF_WEEK) {
      if ((mask & day.value) !== 0) return day.index;
    }
    return -1;
  }

  private getLastDayIndexInMask(mask: number): number {
    for (let i = DL_DAYS_OF_WEEK.length - 1; i >= 0; i--) {
      const day = DL_DAYS_OF_WEEK[i];
      if ((mask & day.value) !== 0) return day.index;
    }
    return -1;
  }

  private isDailyRecurringCycle(program: DlProgram): boolean {
    const daymask = (Number(program.daymask) || 0) & 0x7f;
    const endDaymask = this.getCycleEndDaymask(program);
    return daymask === endDaymask;
  }

  private isDayInCycleMask(dayIndex: number, daymask: number): boolean {
    const dayBit = DL_DAYS_OF_WEEK[dayIndex]?.value;
    if (!dayBit) return false;
    return (daymask & dayBit) !== 0;
  }

  private isDayInCycleSpan(
    dayIndex: number,
    startDayIndex: number,
    endDayIndex: number
  ): boolean {
    if (startDayIndex < 0 || endDayIndex < 0) return false;

    if (startDayIndex === endDayIndex) {
      return dayIndex === startDayIndex;
    }

    if (startDayIndex > endDayIndex) {
      return dayIndex >= startDayIndex || dayIndex <= endDayIndex;
    }

    return dayIndex >= startDayIndex && dayIndex <= endDayIndex;
  }

  private getCycleElapsedSecondsAtMinute(
    dayIndex: number,
    minute: number,
    startDayIndex: number,
    endDayIndex: number,
    startMinute: number
  ): number {
    let currentWeekMinute = (dayIndex * DL_MINUTES_PER_DAY) + minute;
    const anchorWeekMinute = (startDayIndex * DL_MINUTES_PER_DAY) + startMinute;

    if (startDayIndex > endDayIndex && dayIndex <= endDayIndex) {
      currentWeekMinute += DL_WEEK_MINUTES;
    }

    return Math.max(0, (currentWeekMinute - anchorWeekMinute) * 60);
  }

  private getCycleMinuteRangesForDay(
    program: DlProgram,
    dayIndex: number,
    timing: { startMinute: number; endMinute: number; overnight: boolean }
  ): CycleDayRange[] {
    const daymask = Number(program.daymask) || 0;
    const endDaymask = this.getCycleEndDaymask(program);
    const startDayIndex = this.getFirstDayIndexInMask(daymask);
    const endDayIndex = this.getLastDayIndexInMask(endDaymask);

    if (this.isDailyRecurringCycle(program)) {
      if (!this.isDayInCycleMask(dayIndex, daymask)) {
        return [];
      }

      if (!timing.overnight) {
        return [{ start: timing.startMinute, end: timing.endMinute, segment: 'both' }];
      }

      return [
        { start: 0, end: timing.endMinute, segment: 'end' },
        { start: timing.startMinute, end: DL_MINUTES_PER_DAY, segment: 'start' },
      ];
    }

    if (!this.isDayInCycleSpan(dayIndex, startDayIndex, endDayIndex)) {
      return [];
    }

    if (startDayIndex === endDayIndex && !timing.overnight) {
      return [{ start: timing.startMinute, end: timing.endMinute, segment: 'both' }];
    }

    const isStartDay = dayIndex === startDayIndex;
    const isEndDay = dayIndex === endDayIndex;

    if (isStartDay && isEndDay) {
      if (timing.overnight) {
        return [{ start: timing.startMinute, end: DL_MINUTES_PER_DAY, segment: 'start' }];
      }
      return [{ start: timing.startMinute, end: timing.endMinute, segment: 'both' }];
    }

    if (isStartDay) {
      return [{ start: timing.startMinute, end: DL_MINUTES_PER_DAY, segment: 'start' }];
    }

    if (isEndDay) {
      return [{ start: 0, end: timing.endMinute, segment: 'end' }];
    }

    return [{ start: 0, end: DL_MINUTES_PER_DAY, segment: 'middle' }];
  }

  private getCyclePhaseOffsetSeconds(
    program: DlProgram,
    dayIndex: number,
    range: CycleDayRange,
    startDayIndex: number,
    endDayIndex: number,
    startMinute: number
  ): number {
    if (this.isDailyRecurringCycle(program)) {
      if (range.segment === 'end') {
        return (DL_MINUTES_PER_DAY - startMinute) * 60;
      }
      return 0;
    }

    return this.getCycleElapsedSecondsAtMinute(
      dayIndex,
      range.start,
      startDayIndex,
      endDayIndex,
      startMinute
    );
  }

  private applyCycleOverlaysForDay(
    data: number[],
    offset: number,
    programs: DlProgram[],
    dayIndex: number,
    activeFestive: number,
    channelFilter: 1 | 2
  ): void {
    (programs || []).forEach((program) => {
      if (!this.isCycleProgram(program)) return;
      if (!this.matchesChannelAndFestive(program, activeFestive, channelFilter)) return;

      const timing = this.resolveCycleTiming(program);
      if (!timing) return;

      const ranges = this.getCycleMinuteRangesForDay(program, dayIndex, timing);
      if (!ranges.length) return;

      const { startDayIndex, endDayIndex } = this.resolveCycleDayIndices(program);
      const onSeconds = Math.max(1, timing.onSeconds);
      const offSeconds = Math.max(1, timing.offSeconds);
      const period = onSeconds + offSeconds;

      ranges.forEach((range) => {
        const phaseOffset = this.getCyclePhaseOffsetSeconds(
          program,
          dayIndex,
          range,
          startDayIndex,
          endDayIndex,
          timing.startMinute
        );
        for (let minute = range.start; minute < range.end; minute++) {
          data[offset + minute] = this.getCycleValueAtElapsedSeconds(
            phaseOffset + ((minute - range.start) * 60) + 30,
            onSeconds,
            offSeconds,
            period
          );
        }
      });
    });
  }

  private applyPulseOverlaysForDay(
    data: number[],
    offset: number,
    programs: DlProgram[],
    targetDayBit: number,
    activeFestive: number,
    channelFilter: 1 | 2
  ): void {
    (programs || []).forEach((program) => {
      if (!this.isPulseProgram(program)) return;
      if (!this.matchesProgramFilters(program, targetDayBit, activeFestive, channelFilter)) return;

      const range = this.getPulseMinuteRange(program);
      if (!range) return;

      for (let minute = range.start; minute < range.end; minute++) {
        data[offset + minute] = 1;
      }
    });
  }

  private expandPulseEvents(program: DlProgram): TimelineEvent[] {
    const range = this.getPulseMinuteRange(program);
    if (!range) return [];

    const events: TimelineEvent[] = [
      { minute: range.start, value: 1, priority: 30 },
    ];

    if (range.end < DL_MINUTES_PER_DAY) {
      events.push({ minute: range.end, value: 0, priority: 20 });
    }

    return events;
  }

  private expandSwitchEvent(program: DlProgram): TimelineEvent[] {
    const startMinute = this.resolveStartMinute(program);
    if (startMinute === null) return [];

    return [{
      minute: startMinute,
      value: program.kind === 'on' ? 1 : 0,
      priority: 1,
    }];
  }

  private buildEventsForDay(
    programs: DlProgram[],
    targetDayBit: number,
    activeFestive: number,
    channelFilter: 1 | 2
  ): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    (programs || []).forEach((program) => {
      if (!this.matchesProgramFilters(program, targetDayBit, activeFestive, channelFilter)) return;

      if (this.isPulseProgram(program)) {
        events.push(...this.expandPulseEvents(program));
        return;
      }

      if (this.isCycleProgram(program)) {
        // El patrón ON/OFF del ciclo se aplica en applyCycleOverlaysForDay.
        return;
      }

      events.push(...this.expandSwitchEvent(program));
    });

    events.sort((a, b) => (a.minute - b.minute) || (a.priority - b.priority));
    return events;
  }

  /** Convierte un programa al array crudo del firmware (19 bytes). */
  programToRaw(program: {
    id: number;
    channel: number;
    type: number;
    mode: number;
    daymask: number;
    festive: number;
    time: number;
    auxdata?: number[];
  }): number[] {
    const raw = new Array(19).fill(0);
    const time = Math.trunc(Number(program.time) || 0);

    raw[0] = (time >> 24) & 0xff;
    raw[1] = (time >> 16) & 0xff;
    raw[2] = (time >> 8) & 0xff;
    raw[3] = time & 0xff;
    raw[4] = program.id;
    raw[5] = program.channel;
    raw[6] = program.type;
    raw[7] = program.mode;
    raw[8] = program.daymask;
    raw[9] = program.festive;

    const aux = program.auxdata || [];
    for (let i = 0; i < 9; i++) {
      raw[10 + i] = Number(aux[i]) || 0;
    }

    return raw;
  }

  getStartMinute(program: DlProgram): number | null {
    return this.resolveStartMinute(program);
  }

  getPulseDurationSeconds(program: DlProgram): number {
    return this.resolvePulseDurationSeconds(program);
  }

  getCycleEndMinute(program: DlProgram): number | null {
    return this.resolveEndMinute(program.auxdata);
  }

  getCycleOnOffSeconds(program: DlProgram): { onSeconds: number; offSeconds: number } {
    return {
      onSeconds: this.unpackSeconds24(program.auxdata[3], program.auxdata[4], program.auxdata[5]),
      offSeconds: this.unpackSeconds24(program.auxdata[6], program.auxdata[7], program.auxdata[8]),
    };
  }

  /** Valor ON/OFF del ciclo en un minuto; null si ese minuto no pertenece a ningún ciclo. */
  getCycleChartValueAtMinute(
    programs: DlProgram[],
    dayIndex: number,
    minuteOfDay: number,
    activeFestive: number,
    channelFilter: 1 | 2
  ): 0 | 1 | null {
    for (const program of programs || []) {
      if (!this.isCycleProgram(program)) continue;
      if (!this.matchesChannelAndFestive(program, activeFestive, channelFilter)) continue;

      const timing = this.resolveCycleTiming(program);
      if (!timing) continue;

      const ranges = this.getCycleMinuteRangesForDay(program, dayIndex, timing);
      for (const range of ranges) {
        if (minuteOfDay < range.start || minuteOfDay >= range.end) continue;

        const { startDayIndex, endDayIndex } = this.resolveCycleDayIndices(program);
        const onSeconds = Math.max(1, timing.onSeconds);
        const offSeconds = Math.max(1, timing.offSeconds);
        const period = onSeconds + offSeconds;
        const phaseOffset = this.getCyclePhaseOffsetSeconds(
          program,
          dayIndex,
          range,
          startDayIndex,
          endDayIndex,
          timing.startMinute
        );
        return this.getCycleValueAtElapsedSeconds(
          phaseOffset + ((minuteOfDay - range.start) * 60) + 30,
          onSeconds,
          offSeconds,
          period
        );
      }
    }

    return null;
  }

  private getCycleValueAtElapsedSeconds(
    elapsedSeconds: number,
    onSeconds: number,
    offSeconds: number,
    period = onSeconds + offSeconds
  ): 0 | 1 {
    const safePeriod = Math.max(1, period);
    const phase = ((elapsedSeconds % safePeriod) + safePeriod) % safePeriod;
    return phase < onSeconds ? 1 : 0;
  }

  encodeSeconds24(seconds: number): [number, number, number] {
    const safe = Math.max(0, Math.trunc(Number(seconds) || 0));
    return [
      (safe >> 16) & 0xff,
      (safe >> 8) & 0xff,
      safe & 0xff,
    ];
  }

  /** Codifica la hora de fin del ciclo como minutos totales del día (uint16 BE en auxdata[1..2]). */
  encodeEndMinuteBytes(minute: number): [number, number] {
    const safe = this.clampMinute(minute);
    return [(safe >> 8) & 0xff, safe & 0xff];
  }

  /** Asigna el mode exclusivo del firmware según el type del programa. */
  normalizeModeForType(type: number): number {
    if (type === 2) return 64;   // pulso
    if (type === 3) return 128;  // ciclo
    if (type === 1) return 16;   // hora fija (ON)
    return 0;                    // OFF
  }

  resolveKindFromType(type: number, mode = 0): DlProgramKind {
    return this.resolveProgramKind(type, mode);
  }
}
